import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createSportSchema, type CreateSportInput } from '@proref/schemas';
import type { Sport } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { DataPagination } from '../../components/shared/DataPagination';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import {
  useSportsQuery,
  useCreateSport,
  useUpdateSport,
  useDeleteSport,
} from '../../hooks/use-sports';
import { usePagination } from '../../hooks/use-pagination';

// How many sports per page in the catalogue table (the pager hides itself below this count).
const SPORTS_PAGE_SIZE = 8;

// Derive a URL-friendly slug from the sport name (matches createSportSchema's allowed shape).
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Add or edit a sport in a shadcn AlertDialog. One dialog serves both flows: `sport` present →
// edit that row (PATCH by id); absent → create. super_admin only — the route is gated, and this
// page only renders inside the super_admin console.
function SportFormDialog({ sport, trigger }: { sport?: Sport; trigger: ReactNode }) {
  const { t } = useTranslation();
  const isEdit = Boolean(sport);
  const createSport = useCreateSport();
  const updateSport = useUpdateSport();
  const [open, setOpen] = useState(false);
  // Auto-fill the slug from the name until the admin edits it — but only when creating. On an
  // edit the slug already exists, so mirroring it from the name would clobber a deliberate value.
  const [slugTouched, setSlugTouched] = useState(false);

  const defaults: CreateSportInput = sport
    ? { name: sport.name, slug: sport.slug, rulebook_name: sport.rulebook_name }
    : { name: '', slug: '', rulebook_name: '' };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateSportInput>({ resolver: zodResolver(createSportSchema), defaultValues: defaults });

  const nameField = register('name');
  const slugField = register('slug');
  const pending = createSport.isPending || updateSport.isPending;

  // Reset transient UI state whenever the dialog closes (cancel, escape, overlay click).
  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      reset(defaults);
      setSlugTouched(false);
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (sport) {
        await updateSport.mutateAsync({ id: sport.id, input: values });
        notifySuccess(t('admin.sports.updatedToast', { name: values.name }));
      } else {
        const created = await createSport.mutateAsync(values);
        notifySuccess(t('admin.sports.addedToast', { name: created.name }));
      }
      onOpenChange(false);
    } catch (err) {
      notifyError(err, isEdit ? t('admin.sports.updateErrorToast') : t('admin.sports.addErrorToast'));
    }
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isEdit ? t('admin.sports.editTitle', { name: sport!.name }) : t('admin.sports.addTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.sports.formDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
          <Field label={t('admin.federations.fieldName')} htmlFor="sport-name" error={errors.name?.message}>
            <Input
              id="sport-name"
              placeholder={t('admin.sports.namePlaceholder')}
              {...nameField}
              onChange={(e) => {
                nameField.onChange(e);
                // Mirror the slug from the name only while creating and untouched.
                if (!isEdit && !slugTouched) {
                  setValue('slug', slugify(e.target.value), { shouldValidate: true });
                }
              }}
            />
          </Field>
          <Field label={t('admin.sports.fieldSlug')} htmlFor="sport-slug" error={errors.slug?.message}>
            <Input
              id="sport-slug"
              placeholder={t('admin.sports.slugPlaceholder')}
              className="font-mono"
              {...slugField}
              onChange={(e) => {
                setSlugTouched(true);
                slugField.onChange(e);
              }}
            />
          </Field>
          <Field label={t('admin.sports.fieldRulebookName')} htmlFor="sport-rulebook" error={errors.rulebook_name?.message}>
            <Input
              id="sport-rulebook"
              placeholder={t('admin.sports.rulebookPlaceholder')}
              {...register('rulebook_name')}
            />
          </Field>

          <AlertDialogFooter className="mt-1">
            <AlertDialogCancel type="button" disabled={pending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <Button type="submit" variant="accent" disabled={pending}>
              {pending ? (
                <Spinner className="w-4 h-4" />
              ) : isEdit ? (
                t('admin.users.editDialog.saveChanges')
              ) : (
                t('admin.sports.addSport')
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Delete-confirm for a sport. The API refuses (409 SPORT_IN_USE) when the sport still has clips,
// referees or exams — that message surfaces via notifyError so the admin knows to clear it first.
function DeleteSportDialog({ sport }: { sport: Sport }) {
  const { t } = useTranslation();
  const del = useDeleteSport();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await del.mutateAsync(sport.id);
      notifySuccess(t('admin.sports.deletedToast', { name: sport.name }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.sports.deleteErrorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.sports.deleteAria', { name: sport.name })}>
          <Icon.trash className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.sports.deleteConfirmTitle', { name: sport.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.sports.deleteConfirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={del.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-4 h-4" /> : t('admin.sports.deleteButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// The catalogue, with per-row edit + delete for super_admin.
function SportsList() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useSportsQuery();
  const sports = data ?? [];
  // Paginate client-side — the whole catalogue is fetched in one call; the pager just slices it.
  const { page, setPage, pageCount, pageItems } = usePagination(sports, SPORTS_PAGE_SIZE);

  // First load only; a background refetch (data already present) shows nothing here.
  if (isLoading) {
    return <ListSkeleton rows={4} />;
  }

  // Keep the last-good catalogue on screen if a background refetch fails — only show the error
  // when there's genuinely nothing cached.
  if (isError && !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.sports.couldNotLoad'))}
      </div>
    );
  }

  if (sports.length === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <p className="text-sm text-muted">{t('admin.sports.noSportsYet')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* overflow-x-auto (not -hidden) so the row scrolls on a phone rather than crushing cells. */}
      <div className="overflow-x-auto rounded border border-line bg-surface">
      <table className="w-full min-w-[520px] text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
            <th className="px-4 py-2.5 font-semibold">{t('admin.sports.colSport')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.sports.colSlug')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.sports.colRulebook')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.sports.colAdded')}</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {pageItems.map((sport: Sport) => (
            <tr key={sport.id} className="group border-b border-line last:border-0">
              <td className="px-4 py-2.5 font-medium text-ink">{sport.name}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{sport.slug}</td>
              <td className="px-4 py-2.5 text-ink-2">{sport.rulebook_name}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
                {new Date(sport.created_at).toLocaleDateString()}
              </td>
              {/* Row actions — subtle until hover so the table stays readable at a glance. */}
              <td className="px-4 py-2.5">
                <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <SportFormDialog
                    sport={sport}
                    trigger={
                      <Button variant="ghost" size="sm" icon aria-label={t('admin.sports.editAria', { name: sport.name })}>
                        <Icon.pencil className="w-3.5 h-3.5" />
                      </Button>
                    }
                  />
                  <DeleteSportDialog sport={sport} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <DataPagination page={page} pageCount={pageCount} onPageChange={setPage} />
    </div>
  );
}

export function SportsPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t('admin.sports.title')}
        sub={t('admin.sports.sub')}
        actions={
          <SportFormDialog
            trigger={
              <Button variant="accent">
                <Icon.plus className="w-3.5 h-3.5" /> {t('admin.sports.addSportButton')}
              </Button>
            }
          />
        }
      />

      {/* Rule articles are authored on the Rulebooks page; sports here own the discipline itself. */}
      <div className="mb-5 flex items-start gap-2.5 rounded border border-line bg-bg-2 px-4 py-3">
        <Icon.spark className="mt-0.5 w-4 h-4 flex-none text-accent" />
        <p className="text-[12.5px] text-muted">
          {t('admin.sports.noteBody')}
        </p>
      </div>

      <SportsList />
    </>
  );
}
